"use strict";
/**
 * The cents-native engine.
 *
 * Same formula and same order of operations as ./legacy.ts — rooms, then the
 * frequency discount, then flexible, then promo, then add-ons, then tax. Only
 * the precision changes. Shipping a new rate card and a new arithmetic in one
 * step would make it impossible to tell a migration bug from a pricing
 * decision, so Phase 1 changes precision alone.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTaskPrice = exports.getBookingPrice = exports.nearbySpanPercent = exports.getAddOnsCostCents = exports.getTaxCents = exports.getRoomsCents = void 0;
const rates_1 = require("./rates");
const cleaning_types_1 = require("./cleaning-types");
const arrival_1 = require("./arrival");
const money_1 = require("./money");
const config_1 = require("./config");
/**
 * What a home's rooms add to its base, in cents.
 *
 * The first `bedrooms` entries of the bedroom table plus the first `bathrooms`
 * entries of the bathroom table — each room priced at its own position, so a
 * third bedroom costs what the table says a third bedroom costs.
 *
 * Refuses a count the table has no price for rather than extrapolating. Past
 * the end of the table there is no configured price, and repeating the last one
 * would be the engine inventing a rate an admin never set.
 */
function getRoomsCents(prices, bedrooms, bathrooms) {
    const sumFirst = (table, count, label) => {
        if (!Number.isInteger(count) || count < 0 || count > table.length) {
            throw new RangeError(`Cannot price ${count} ${label}: the room table prices 0 to ` +
                `${table.length}.`);
        }
        return (0, money_1.sumCents)(table.slice(0, count));
    };
    return (sumFirst(prices.bedroom, bedrooms, "bedrooms") +
        sumFirst(prices.bathroom, bathrooms, "bathrooms"));
}
exports.getRoomsCents = getRoomsCents;
/** Sales tax on a subtotal, in cents. */
function getTaxCents(subTotalCents) {
    return (0, money_1.applyRate)(subTotalCents, rates_1.TAX_RATE);
}
exports.getTaxCents = getTaxCents;
/**
 * Add-on cost in cents.
 *
 * Note the free-extras rule is now decided by cleaning *type*, not by comparing
 * a truncated price against a threshold. The old comparison silently stops
 * working the moment the threshold carries cents.
 */
function getAddOnsCostCents(cleaningType, addOns, createdAt) {
    const extrasIncluded = (0, cleaning_types_1.includesFreeExtras)(cleaningType, createdAt);
    let cost = 0;
    for (const addOn of addOns || []) {
        const name = addOn.toLowerCase();
        if (name.includes("organizing"))
            cost += rates_1.ADD_ON_COST_CENTS.organizing;
        else if (name.includes("laundry"))
            cost += rates_1.ADD_ON_COST_CENTS.laundry;
        else if (name.includes("windows"))
            cost += rates_1.ADD_ON_COST_CENTS.windows;
        else if (name.includes("cabinet")) {
            if (!extrasIncluded)
                cost += rates_1.ADD_ON_COST_CENTS.cabinets;
        }
        else if (name.trim() && !extrasIncluded) {
            cost += rates_1.ADD_ON_COST_CENTS.oven;
        }
    }
    return cost;
}
exports.getAddOnsCostCents = getAddOnsCostCents;
/**
 * Resolve the day of the week to price against.
 *
 * Returns `null` when the config cannot vary by day and the caller gave no
 * service date, which is the pre-surge status quo: the landing page's "Book from
 * $X" button prices a hypothetical booking that has no service date at all.
 */
function resolveDayOfWeek(input, config) {
    if (input.service_date)
        return (0, config_1.dayOfWeekForServiceDate)(input.service_date);
    if (input.same_day) {
        throw new RangeError("A Same-Day booking must supply service_date: the Same-Day surcharge is " +
            "configured per cleaning type per day of week, and there is no day to " +
            "look up without it.");
    }
    if ((0, config_1.pricingVariesByDayOfWeek)(config)) {
        throw new RangeError("service_date is required to price on this config — day-of-week surge, " +
            "Same-Day surcharge or a per-day Flexible Time discount is set, so the " +
            "price depends on which day the cleaning happens. Defaulting to a day " +
            "would charge one day's rate for another's booking.");
    }
    return null;
}
function resolveFrequencyPercent(input) {
    if (input.frequencyDiscountPercent !== undefined) {
        const percent = input.frequencyDiscountPercent;
        if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
            throw new RangeError(`frequencyDiscountPercent must be 0-100, got ${percent}`);
        }
        return percent;
    }
    if (input.frequency === undefined) {
        throw new RangeError("Supply either frequency or frequencyDiscountPercent");
    }
    return (0, rates_1.getFrequencyDiscountPercent)(input.frequency);
}
/**
 * The full breakdown, in cents.
 *
 * Returns every component rather than a bare total so the order summary, the
 * receipt email, Slack and the dashboard can all *render* the numbers that were
 * charged instead of each recomputing them and drifting. Five independent
 * reimplementations of this arithmetic is how the cart came to tax at 8.88%
 * while the server used 8.875%.
 */
/**
 * The nearby-discount percentage of the span a `clean_time` falls in, or null
 * when it falls in none (or is Flexible Time, or cannot be placed).
 *
 * Throws on a rule set that cannot price an arrival unambiguously -- an empty
 * or inverted span, or two rules covering the same minute -- rather than
 * picking one and charging it. A config that says two things about one arrival
 * is an admin error, and silently taking the first would bill whichever order
 * the rows happened to be stored in.
 */
function nearbySpanPercent(rules, cleanTime, dayOfWeek) {
    if (!Array.isArray(rules) || rules.length === 0)
        return null;
    for (const rule of rules) {
        if (!Number.isFinite(rule.startMinutes) ||
            !Number.isFinite(rule.endMinutes) ||
            rule.endMinutes <= rule.startMinutes) {
            throw new RangeError(`A ${dayOfWeek} time-of-day rule ends at or before it starts ` +
                `(${rule.startMinutes} to ${rule.endMinutes}). A span that covers no ` +
                `minute prices nothing, which reads as the rule simply not applying.`);
        }
    }
    for (let i = 0; i < rules.length; i++) {
        for (let j = i + 1; j < rules.length; j++) {
            const a = rules[i];
            const b = rules[j];
            if (a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes) {
                throw new RangeError(`Two ${dayOfWeek} time-of-day rules overlap (${a.startMinutes}-` +
                    `${a.endMinutes} and ${b.startMinutes}-${b.endMinutes}). One arrival ` +
                    `would match both, and charging either is a coin toss.`);
            }
        }
    }
    const arrival = (0, arrival_1.parseArrival)(cleanTime);
    // Flexible Time, or a time we cannot place: no span. We choose a flexible
    // booking's arrival, so it keeps the normal nearby discount.
    if (!arrival || arrival.kind !== "fixed")
        return null;
    const hit = rules.find((rule) => arrival.minutes >= rule.startMinutes && arrival.minutes < rule.endMinutes);
    return hit ? hit.percent : null;
}
exports.nearbySpanPercent = nearbySpanPercent;
function getBookingPrice(input, promoDiscountCents = 0, 
/**
 * The rates in force for this booking. Defaults to the hardcoded rate card for
 * `created_at` with every surge axis at zero, so a caller that has not been
 * taught to fetch config from the API gets exactly the price it got before
 * this parameter existed.
 */
config = (0, config_1.defaultPricingConfig)(input.created_at)) {
    var _a, _b, _c, _d;
    const nBedrooms = Number(input.bedrooms);
    const nBathrooms = Number(input.bathrooms);
    if (!Number.isInteger(nBedrooms) || !Number.isInteger(nBathrooms)) {
        throw new RangeError("bedrooms and bathrooms must be whole numbers");
    }
    const cleaningTypeCents = config.rateCard.cleaningTypes[input.cleaningType];
    if (cleaningTypeCents === undefined) {
        throw new RangeError(`Unknown cleaning type: ${String(input.cleaningType)}`);
    }
    const flexible = (0, rates_1.isFlexible)(input.clean_time);
    if (flexible && input.same_day) {
        throw new RangeError("A booking cannot be both Same-Day and Flexible Time — Flexible Time is a " +
            "discount for accepting a 9am-4pm window and Same-Day offers its own " +
            "narrowing windows, so the two are mutually exclusive.");
    }
    const dayOfWeek = resolveDayOfWeek(input, config);
    const roomsCents = getRoomsCents(config.rateCard.roomCents, nBedrooms, nBathrooms);
    const baseCents = roomsCents + cleaningTypeCents;
    /**
     * Surge, surcharge and every discount are each a percentage of `baseCents` and
     * are then summed — never applied one after another against a running total.
     * See `docs/adr/0005-surge-and-surcharge-stack-additively.md`: 10% surge plus a
     * 15% Same-Day surcharge is 25% of base, not 15% compounded onto a surged
     * base. This is the shape the frequency and flexible discounts have always
     * had, and matching it is what keeps the breakdown's lines summable.
     */
    const dayOfWeekSurgeCents = dayOfWeek
        ? (0, money_1.percentOfCents)(baseCents, config.dayOfWeekSurgePercent[dayOfWeek], `${dayOfWeek} surge`)
        : 0;
    /**
     * The nearby-discount time span this arrival falls in (25 Sep 2026): its
     * percentage replaces the normal nearby discount below; 0% hides it.
     * Looked up on every booking, so a broken span set throws the day it is
     * saved rather than on the first nearby booking. A span stored with a
     * negative percentage -- an earlier build's "extra discount" -- is ignored,
     * so an old row can never take pricing down.
     */
    const spanPercent = dayOfWeek
        ? nearbySpanPercent((_a = config.timeOfDayRules) === null || _a === void 0 ? void 0 : _a[dayOfWeek], input.clean_time, dayOfWeek)
        : null;
    const spanApplies = spanPercent !== null && spanPercent >= 0 && spanPercent <= 100;
    /**
     * Pricing a Same-Day booking against a config that does not offer Same-Day on
     * that day is a bug in either direction, so it throws rather than returning a
     * surcharge of zero. On the server it means the wrong config was loaded. In the
     * browser it means the caller built a config from the public projection, which
     * deliberately carries no surcharge grid (ADR 0006) — the right move there is
     * to ask the server for a Same-Day price, not to compute one that is missing
     * the surcharge and looks like a bargain.
     */
    if (input.same_day && dayOfWeek && !config.sameDay.enabledByDay[dayOfWeek]) {
        throw new RangeError(`Cannot price a Same-Day booking on ${dayOfWeek}: this config does not ` +
            `offer Same-Day that day. A config built from the public projection ` +
            `never carries the Same-Day surcharge grid — resolve a Same-Day price ` +
            `server-side instead of pricing one at zero surcharge.`);
    }
    const sameDaySurchargeCents = input.same_day && dayOfWeek
        ? (0, money_1.percentOfCents)(baseCents, (_c = (_b = config.sameDaySurchargePercent[input.cleaningType]) === null || _b === void 0 ? void 0 : _b[dayOfWeek]) !== null && _c !== void 0 ? _c : 0, `${input.cleaningType} ${dayOfWeek} Same-Day surcharge`)
        : 0;
    /**
     * Proximity, resolved from the tier the server handed us.
     *
     * Refuses a tier the config does not offer, in both directions and for the
     * same reasons Same-Day does. On the server it means the wrong config was
     * loaded. In the browser it means the caller built a config from the public
     * projection, which never carries proximity (ADR 0009) — and pricing a
     * discount at zero there would show the customer a total the server will not
     * honour, while pricing a *premium* at zero would undercharge outright.
     */
    const tier = (_d = input.proximity_tier) !== null && _d !== void 0 ? _d : "none";
    if (tier === "cluster" && !config.proximity.clusterEnabled) {
        throw new RangeError("Cannot price a nearby-cleaning discount: this config does not offer one. " +
            "A config built from the public projection never carries proximity — " +
            "resolve the tier and its amount server-side and fold it in with " +
            "withProximityTier.");
    }
    if (tier === "travel" && !config.proximity.travelEnabled) {
        throw new RangeError("Cannot price a travel premium: this config does not offer one. Charging " +
            "zero here would quietly drop the premium from a booking the server " +
            "resolved as needing one.");
    }
    const travelPremiumCents = tier === "travel"
        ? (0, money_1.percentOfCents)(baseCents, config.proximity.travelPremiumPercent, "travel premium")
        : 0;
    const addOnsCents = getAddOnsCostCents(input.cleaningType, input.add_ons, input.created_at);
    /**
     * Everything the customer is being charged for, before anything is taken off.
     * Discounts are then clamped against what is left of it, in order.
     *
     * The clamping is not defensive tidiness. Without it a coupon worth more than
     * the booking — a $200 code on a $168 studio — produced a negative subtotal, a
     * negative tax and a negative total, which `toBookingLines` rendered as
     * "Total -$34.84", `linesReconcile` certified as correct, and `toStripeAmount`
     * passed straight to `paymentIntents.create({ amount: -3484 })`. An
     * admin-configurable Flexible Time discount reaches the same place by a
     * shorter route: one unit slip in the editor (1500 meaning $15, typed as
     * 150000) is a discount larger than any booking it applies to.
     *
     * `getTaskPrice` in this same file has always clamped its promo with
     * `Math.min(promo, gross)`; booking pricing simply never did.
     */
    const grossCents = baseCents +
        dayOfWeekSurgeCents +
        sameDaySurchargeCents +
        travelPremiumCents +
        addOnsCents;
    /**
     * What is left to discount, taken down by each discount in turn. Every one is
     * clamped against it, in order, and the subtotal is whatever remains — so a
     * new discount is added in one place rather than appended to three
     * subtraction lists that must be kept in step for the lines to sum.
     */
    let remainingCents = grossCents;
    const takeOff = (cents) => {
        const taken = Math.min(cents, remainingCents);
        remainingCents -= taken;
        return taken;
    };
    const frequencyDiscountCents = takeOff((0, money_1.percentDiscountCents)(baseCents, resolveFrequencyPercent(input)));
    /**
     * `dayOfWeek` is null only when `resolveDayOfWeek` established that this
     * config cannot vary by day, which by `pricingVariesByDayOfWeek` means every
     * day carries the *same* Flexible Time discount. So reading any single day
     * here reads the right number — it is not a guess about which day it is.
     */
    const flexibleDiscountCents = takeOff(flexible ? config.flexibleDiscountCents[dayOfWeek !== null && dayOfWeek !== void 0 ? dayOfWeek : "sunday"] : 0);
    /**
     * The nearby-cleaning discount, taken off after the two the booking's own
     * shape earns and before the coupon.
     *
     * Ordered there because it belongs with them: frequency, Flexible Time and
     * proximity are all properties of the booking we are selling, while a promo
     * code is something the customer brought. Keeping the coupon clamped last
     * leaves it as the line that absorbs the shortfall on a booking whose
     * discounts exceed it, which is the behaviour already pinned by tests.
     *
     * `percentOfCents`, like surge and the travel premium: this is an
     * admin-entered percentage stored in basis points, and may carry a fraction.
     * `percentDiscountCents` is the frequency discount's whole-percent helper.
     */
    const proximityDiscountCents = takeOff(tier === "cluster"
        ? (0, money_1.percentOfCents)(baseCents, 
        // Read only here: a config for a booking that is not nearby -- the
        // admin's price board builds one -- need carry no proximity at all.
        spanApplies ? spanPercent : config.proximity.clusterDiscountPercent, "nearby-cleaning discount")
        : 0);
    /**
     * Reported as the amount actually taken off, not the amount the coupon is
     * worth, so the receipt's lines still sum to the total the card was charged.
     */
    const appliedPromoDiscountCents = takeOff((0, money_1.assertCents)(promoDiscountCents, "promo discount"));
    const subTotalCents = remainingCents;
    const taxCents = getTaxCents(subTotalCents);
    return {
        baseCents,
        roomsCents,
        cleaningTypeCents,
        dayOfWeekSurgeCents,
        sameDaySurchargeCents,
        travelPremiumCents,
        proximityDiscountCents,
        frequencyDiscountCents,
        flexibleDiscountCents,
        promoDiscountCents: appliedPromoDiscountCents,
        addOnsCents,
        subTotalCents,
        taxCents,
        totalCents: subTotalCents + taxCents,
    };
}
exports.getBookingPrice = getBookingPrice;
/**
 * The task checkout total, in cents.
 *
 * Two differences from the engine it replaces. It never converts to dollars, so
 * tax is computed on the exact cent subtotal. And it actually applies the promo
 * discount — the previous implementation accepted a `codeDiscount` argument and
 * never referenced it, so every coupon in the task flow was inert while the
 * checkout still validated the code and rejected bad ones.
 */
function getTaskPrice(tasks, { promoDiscountCents = 0, tipCents = 0, creditsCents = 0, } = {}) {
    const gross = (0, money_1.sumCents)((tasks || []).map((task) => task.price_cents));
    const promo = Math.min((0, money_1.assertCents)(promoDiscountCents, "promo discount"), gross);
    const subTotalCents = gross - promo;
    const taxCents = getTaxCents(subTotalCents);
    const totalCents = subTotalCents + taxCents + (0, money_1.assertCents)(tipCents, "tip");
    const creditsAppliedCents = Math.min((0, money_1.assertCents)(creditsCents, "credits"), totalCents);
    return {
        subTotalCents,
        promoDiscountCents: promo,
        taxCents,
        tipCents,
        totalCents,
        creditsAppliedCents,
        amountDueCents: totalCents - creditsAppliedCents,
    };
}
exports.getTaskPrice = getTaskPrice;
//# sourceMappingURL=decimal.js.map